import { components } from '@/types/api';
import PrimaryAccountLogo from '@/assets/icons/primary_account_logo.svg?react';
import MoreVert from '@/assets/icons/more_vert.svg?react';
import { cn, ColoredTileIndex, coloredTileVariants } from '@/lib/utils';
import { Button } from '../ui/button';
import { Link } from 'wouter';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

export function AccountTile({
  account,
  index,
}: {
  account: components['schemas']['AccountOutput'];
  index: number;
}) {
  return (
    <Link href={`/account/${account.id}`} className="overflow-clip">
      {account.is_primary ? (
        <PrimaryAccountTile account={account} />
      ) : (
        <SecondaryAccountTile account={account} index={index} />
      )}
    </Link>
  );
}

function PrimaryAccountTile({ account }: { account: components['schemas']['AccountOutput'] }) {
  return (
    <div
      key={account.id}
      className={cn(
        'flex w-[calc(100vw-2rem)] max-w-[360px] items-center justify-between gap-2',
        'border-primary-dark rounded-[100px_0px_0px_100px] border border-r-0 bg-white px-4 py-3 pr-0',
        'animate-in slide-in-from-right ease-out-expo fill-mode-forwards opacity-0 delay-300 duration-500',
      )}
    >
      <PrimaryAccountLogo />
      <div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="text-foreground w-40 truncate text-2xl font-bold">{account.nickname}</p>
            </TooltipTrigger>
            <TooltipContent>{account.nickname}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <span className="text-primary-dark text-sm leading-4 font-normal opacity-50">Primary</span>
      </div>
      <p className="text-foreground text-xl leading-10 font-bold">${account.balance}</p>
      <Link href={`?account=${account.id}`} asChild replace>
        <Button variant="ghost" size="icon" className="h-12 w-12 [&_svg]:size-12">
          <MoreVert />
        </Button>
      </Link>
    </div>
  );
}

function SecondaryAccountTile({
  account,
  index,
}: {
  account: components['schemas']['AccountOutput'];
  index: number;
}) {
  const displayBalance =
    account.type === 'loyalty_program' ? `${account.balance} pts` : `$${account.balance}`;

  return (
    <div
      key={account.id}
      className={cn(
        'flex w-[calc(100vw-4rem)] max-w-[322px] items-center justify-between gap-2',
        'rounded-[62px_0px_0px_62px] border-r-0 border-none bg-white/65 px-2 py-1 pr-0 shadow-[4px_-2px_0px_rgba(255,255,255,0.64)]',
        'animate-in slide-in-from-right ease-out-expo fill-mode-forwards delay-700 duration-500',
      )}
    >
      <span
        className={cn(
          'bg-secondary-light flex h-16 w-16 items-center justify-center rounded-full p-1 lowercase',
          coloredTileVariants({ index: (index % 12) as ColoredTileIndex }),
        )}
      >
        {account.nickname[0]}
      </span>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <p className="text-foreground w-32 truncate text-xl leading-10 font-extralight">
              {account.nickname}
            </p>
          </TooltipTrigger>
          <TooltipContent>{account.nickname}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <p className="text-foreground text-xl leading-10 font-extralight whitespace-nowrap">
        {displayBalance}
      </p>
      <Link href={`?account=${account.id}`} asChild replace>
        <Button variant="ghost" size="icon" className="h-12 w-10 p-0 [&_svg]:size-12">
          <MoreVert />
        </Button>
      </Link>
    </div>
  );
}
